import './ChipGroup.css';

export interface ChipGroupOption<T extends string> {
  value: T;
  label: string;
  /** Optional HTML title — useful when `label` is a short form and the
   *  full text should appear on hover. */
  title?: string;
}

export type ChipGroupVariant = 'segmented' | 'pills';

export interface ChipGroupProps<T extends string> {
  /** Currently-selected value. One of `options[i].value`. */
  value: T;
  options: ChipGroupOption<T>[];
  onChange: (value: T) => void;
  /** Visual variant:
   *  - 'segmented' (default): connected pill group, no wrap. Best for 2-4
   *    short options that act like a single control.
   *  - 'pills': floating rounded chips that wrap to multiple lines. Best
   *    for larger sets where each chip stands on its own. */
  variant?: ChipGroupVariant;
  /** Optional aria label for the radiogroup wrapper. */
  ariaLabel?: string;
  /** Disable the entire group. */
  disabled?: boolean;
  /** Extra class on the root element. */
  className?: string;
}

/**
 * Single-select chip group. Two visual variants — `segmented` (connected,
 * no-wrap) and `pills` (floating, wrappable). Both share the same API and
 * radiogroup semantics. For larger option sets where chips don't fit, prefer
 * a `<select>` dropdown.
 */
export function ChipGroup<T extends string>({
  value, options, onChange, variant = 'segmented', ariaLabel, disabled, className,
}: ChipGroupProps<T>) {
  return (
    <div
      className={['cg', `cg--${variant}`, className].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`cg-chip ${value === o.value ? 'cg-chip--active' : ''}`}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          title={o.title}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
